/**
 * Закрытие верхнего открытого слоя (модалка, дропдаун, меню) по Esc.
 *
 * 29.09: раньше Esc слушала только общая Modal, а модалка «Маржа и плечо»,
 * «Слои» над графиком, «Меню» в шапке, колокольчик и мобильное «Ещё» были
 * самописными и на Esc не реагировали. Если каждый повесит свой keydown, то
 * одно нажатие закроет всё сразу (например, модалку и дропдаун под ней).
 * Поэтому — стек: слой регистрируется, пока открыт, и Esc получает только
 * последний открытый. Слушатель один на документ.
 */
import { useEffect, useRef } from 'react';

type Entry = { close: () => void };
const stack: Entry[] = [];

function onKeyDown(e: KeyboardEvent) {
  if (e.key !== 'Escape' || stack.length === 0) return;
  // Esc уже обработан кем-то ниже (например, автокомплит закрыл свой список
  // и вызвал preventDefault) — второй слой из-за того же нажатия не закрываем.
  if (e.defaultPrevented) return;
  e.preventDefault();
  stack[stack.length - 1].close();
}

export function useEscape(active: boolean, onClose: () => void) {
  // Колбэк через ref: вызывающие передают стрелки, и без ref слой
  // перерегистрировался бы на каждый рендер и «всплывал» бы наверх стека.
  const ref = useRef(onClose);
  ref.current = onClose;
  useEffect(() => {
    if (!active) return;
    const entry: Entry = { close: () => ref.current() };
    if (stack.length === 0) document.addEventListener('keydown', onKeyDown);
    stack.push(entry);
    return () => {
      const i = stack.indexOf(entry);
      if (i >= 0) stack.splice(i, 1);
      if (stack.length === 0) document.removeEventListener('keydown', onKeyDown);
    };
  }, [active]);
}
